// Chat UI: a fade-in history overlay plus an inline input box that
// becomes active when the user presses Enter (all-chat) or Y / T
// (allies-only chat). Tab toggles scope while the box is open.
//
// Keyboard suppression is handled in keyboard.ts: as long as an
// HTMLInputElement is focused, the global tank-key handler is skipped.

import { CHAT_MAX_LENGTH, type ChatScope, type S2CChat } from '@bolo2k/shared';
import { ownerColor } from '@bolo2k/client-core';
import type { Connection } from '@bolo2k/client-core';
import type { Keyboard } from './keyboard.js';

interface ChatLine extends S2CChat {
  receivedAt: number; // performance.now() at receive — drives fade-out
}

const HISTORY_LIMIT = 8;
// Lines fade out this long after arrival (ms). Ample time to read across
// the table without crowding the screen.
const FADE_AFTER_MS = 12_000;
const FADE_DURATION_MS = 1_500;

export class ChatUI {
  private conn: Connection | null = null;
  private keyboard: Keyboard | null = null;
  // Callback for "system feedback" messages (alliance command results,
  // unrecognised commands, etc.). Routed to the news wire by main.ts.
  private onFeedback: ((text: string) => void) | null = null;
  private isOpen = false;
  private scope: ChatScope = 'all';
  private readonly messages: ChatLine[] = [];
  // Latest snapshot's playerNames; consulted when rendering the "from" tag.
  private playerNames: string[] = [];
  // Spectators see chat history but can't open the input box (the server
  // would silently drop their messages anyway).
  private spectator = false;
  private readonly history: HTMLElement;
  private readonly box: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly scopeBadge: HTMLSpanElement;
  private fadeTimer: ReturnType<typeof setInterval> | null = null;
  // Signature of the last-rendered history (message identities + resolved
  // names + rounded opacities). The 250 ms fade timer skips the full
  // innerHTML rebuild when nothing visible changed — which is every tick
  // while messages sit in their 12 s full-opacity window, and all of them
  // once the history is empty. '' = empty history (matches initial DOM).
  private lastHistorySig = '';

  constructor(root: HTMLElement) {
    root.hidden = false;
    root.innerHTML = '';

    this.history = document.createElement('div');
    this.history.className = 'chat-history';
    root.appendChild(this.history);

    this.box = document.createElement('div');
    this.box.className = 'chat-box';
    this.box.style.display = 'none';
    this.scopeBadge = document.createElement('span');
    this.scopeBadge.className = 'chat-scope';
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.maxLength = CHAT_MAX_LENGTH;
    this.input.placeholder = 'message…';
    this.input.spellcheck = false;
    this.input.autocomplete = 'off';
    this.box.appendChild(this.scopeBadge);
    this.box.appendChild(this.input);
    root.appendChild(this.box);

    this.input.addEventListener('keydown', (e) => this.onInputKey(e));
    this.input.addEventListener('blur', () => {
      // If focus is lost without Enter/Escape (e.g. user clicked the canvas),
      // close cleanly so the next Enter re-opens with empty state.
      if (this.isOpen) this.close();
    });
    // Repaint fade-out every 250 ms while messages are visible.
    this.fadeTimer = setInterval(() => this.renderHistory(), 250);
  }

  attach(conn: Connection, keyboard: Keyboard, onFeedback: (text: string) => void): void {
    this.conn = conn;
    this.keyboard = keyboard;
    this.onFeedback = onFeedback;
  }

  /** Hand off a global keydown event when chat is closed. Returns true
   *  if the event was consumed (chat is now opening), false otherwise. */
  handleGlobalKey(e: KeyboardEvent): boolean {
    if (this.isOpen) return false;
    if (this.spectator) return false;
    // Don't steal Y/T/Enter when the user is typing in another text field
    // (the lobby callsign input is the obvious case — otherwise typing "y"
    // there opens this chat overlay and yanks focus). Mirrors the same
    // guard keyboard.ts uses for the tank-input bindings.
    const target = e.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return false;
    }
    // Some browsers fire Enter on form submission — only act on the bare
    // physical key with no modifiers.
    if (e.code === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      this.open('all');
      e.preventDefault();
      return true;
    }
    if ((e.code === 'KeyY' || e.code === 'KeyT') && !e.ctrlKey && !e.metaKey) {
      this.open('allies');
      e.preventDefault();
      return true;
    }
    return false;
  }

  applyChatMessage(msg: S2CChat): void {
    this.messages.push({ ...msg, receivedAt: performance.now() });
    if (this.messages.length > HISTORY_LIMIT) this.messages.shift();
    this.renderHistory();
  }

  setPlayerNames(names: string[]): void {
    this.playerNames = names;
  }

  setSpectator(spectating: boolean): void {
    this.spectator = spectating;
  }

  // ---- internals ---------------------------------------------------

  private open(scope: ChatScope): void {
    this.scope = scope;
    this.isOpen = true;
    this.box.style.display = 'flex';
    this.input.value = '';
    this.updateScopeBadge();
    // Drop any keys held when chat opened (W from "what's up?", etc.).
    this.keyboard?.clear();
    // Defer focus until the click-or-key handler that triggered us has
    // finished — Safari will otherwise immediately blur it.
    queueMicrotask(() => this.input.focus());
  }

  private close(): void {
    this.isOpen = false;
    this.box.style.display = 'none';
    this.input.value = '';
    this.input.blur();
  }

  private onInputKey(e: KeyboardEvent): void {
    e.stopPropagation();
    if (e.code === 'Enter') {
      e.preventDefault();
      this.submit();
      return;
    }
    if (e.code === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.code === 'Tab') {
      e.preventDefault();
      this.scope = this.scope === 'all' ? 'allies' : 'all';
      this.updateScopeBadge();
      return;
    }
  }

  private submit(): void {
    const text = this.input.value.trim();
    if (!text) {
      this.close();
      return;
    }
    // Slash-command dispatch — alliance management lives in chat now that
    // the alliance panel is gone. Recognised commands never go to other
    // players; they're intercepted here and dispatched directly.
    if (text.startsWith('/')) {
      this.dispatchCommand(text);
      this.close();
      return;
    }
    if (this.conn) this.conn.sendChat(text, this.scope);
    this.close();
  }

  private dispatchCommand(text: string): void {
    const match = text.match(/^\/(\w+)\s*(.*)$/);
    if (!match) {
      this.onFeedback?.(`unknown command: ${text}`);
      return;
    }
    const cmd = match[1]!.toLowerCase();
    const arg = (match[2] ?? '').trim();
    switch (cmd) {
      case 'ally':
      case 'unally': {
        if (!arg) {
          this.onFeedback?.(`/${cmd} <player name> — name required`);
          return;
        }
        const slot = this.findPlayerSlot(arg);
        if (slot < 0) {
          this.onFeedback?.(`/${cmd}: no player named "${arg}"`);
          return;
        }
        if (!this.conn) return;
        if (cmd === 'ally') {
          this.conn.requestAlly(slot);
          this.onFeedback?.(`alliance requested with ${this.playerNames[slot] ?? `P${slot}`}`);
        } else {
          this.conn.cancelAlly(slot);
          this.onFeedback?.(`alliance cancelled with ${this.playerNames[slot] ?? `P${slot}`}`);
        }
        return;
      }
      default:
        this.onFeedback?.(`unknown command: /${cmd}`);
    }
  }

  // Case-insensitive exact match against the snapshot's playerNames.
  // Returns slot index (0..MAX_PLAYERS-1), or -1 if no match.
  private findPlayerSlot(name: string): number {
    const target = name.toLowerCase();
    for (let i = 0; i < this.playerNames.length; i++) {
      const n = this.playerNames[i];
      if (n && n.toLowerCase() === target) return i;
    }
    return -1;
  }

  private updateScopeBadge(): void {
    this.scopeBadge.textContent = this.scope === 'all' ? '[All]' : '[Allies]';
    this.scopeBadge.classList.toggle('chat-scope-allies', this.scope === 'allies');
  }

  private renderHistory(): void {
    const now = performance.now();
    // Drop fully-faded entries.
    while (this.messages.length > 0) {
      const head = this.messages[0]!;
      if (now - head.receivedAt < FADE_AFTER_MS + FADE_DURATION_MS) break;
      this.messages.shift();
    }
    // Cheap change detection before touching the DOM. receivedAt is the
    // message identity (its text/from/scope never change after receipt);
    // the resolved name is included because a late playerNames update can
    // change how the "from" tag renders; opacity is rounded to 2 dp so the
    // fade still repaints every 250 ms tick during its 1.5 s ramp while the
    // full-opacity window compares equal.
    const sig = this.messages
      .map((m) => {
        const age = now - m.receivedAt;
        const opacity = age < FADE_AFTER_MS
          ? 1
          : Math.max(0, 1 - (age - FADE_AFTER_MS) / FADE_DURATION_MS);
        const name = this.playerNames[m.from] || `P${m.from}`;
        return `${m.receivedAt}:${name}:${opacity.toFixed(2)}`;
      })
      .join('|');
    if (sig === this.lastHistorySig) return;
    this.lastHistorySig = sig;
    this.history.innerHTML = '';
    for (const m of this.messages) {
      const age = now - m.receivedAt;
      const opacity = age < FADE_AFTER_MS
        ? 1
        : Math.max(0, 1 - (age - FADE_AFTER_MS) / FADE_DURATION_MS);
      const line = document.createElement('div');
      line.className = `chat-line ${m.scope === 'allies' ? 'allies' : 'all'}`;
      line.style.opacity = String(opacity);
      const tag = document.createElement('span');
      tag.className = 'chat-from';
      tag.style.color = ownerColor(m.from);
      const name = this.playerNames[m.from] || `P${m.from}`;
      tag.textContent = `${name}${m.scope === 'allies' ? ' [allies]' : ''}:`;
      const txt = document.createElement('span');
      txt.textContent = ` ${m.text}`;
      line.appendChild(tag);
      line.appendChild(txt);
      this.history.appendChild(line);
    }
  }
}
